import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Payment() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ live: false, scan_fee_cents: 7900, currency: "ZAR" }));
  }, []);

  const amount = config ? (config.scan_fee_cents / 100).toFixed(2) : "79.00";

  const pay = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/create");
      if (data.paylink_url) {
        window.location.href = data.paylink_url; // redirect to iKhokha hosted page (card entry happens there)
      } else if (data.demo) {
        // No live iKhokha keys yet — simulate the hosted-page success.
        await api.post(`/payments/${data.payment_id}/confirm-demo`);
        toast.success("Payment confirmed.");
        navigate("/scan", { state: { paymentId: data.payment_id } });
      } else {
        toast.error("Could not start payment. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-2xl border border-charcoal-border bg-charcoal-surface p-8 md:p-10"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-gold">Secure Checkout</span>
          <h1 className="mt-2 font-heading text-4xl text-cream">Scan fee</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            A single fee unlocks one full scalp analysis and report.
          </p>

          <div className="my-8 flex items-end justify-between border-y border-charcoal-border py-6">
            <span className="text-muted-foreground">Carlocs Scalp Scan</span>
            <span className="font-heading text-4xl text-cream">R{amount}</span>
          </div>

          <div className="mb-6 flex items-start gap-2 rounded-lg border border-charcoal-border bg-charcoal p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
            {config && !config.live
              ? "Demo mode: iKhokha isn't live yet, so this simulates a successful payment. Once keys are added you'll be taken to iKhokha's secure page to enter your card."
              : "You'll be redirected to iKhokha's secure hosted page to enter your card details. Carlocs never sees or stores your card information."}
          </div>

          <Button
            onClick={pay} disabled={processing || !config}
            className="w-full rounded-full bg-gold py-6 text-base font-500 text-charcoal hover:bg-gold/90"
            data-testid="pay-securely-btn"
          >
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> Pay securely · R{amount}</>}
          </Button>

          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" /> Payments processed by iKhokha
          </p>
        </motion.div>
      </div>
    </div>
  );
}
